import { useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { http } from "@/lib/api";
import { ArrowUp, ArrowDown } from "@phosphor-icons/react";

export default function EnvironmentalIndicators() {
  const [metrics, setMetrics] = useState([]);
  useEffect(() => {
    http.get("/indicators").then(r => setMetrics(r.data.metrics || [])).catch(() => {});
  }, []);

  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <section ref={ref} data-testid="indicators-section" className="relative py-24 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-4">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/02 · The Signals</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            The Earth,<br/><em className="text-[color:var(--np-live)]">in numbers.</em>
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 text-[color:var(--np-text-2)] text-lg self-end">
          Twelve indicators, updated from authoritative sources — WMO, NASA, NOAA, IUCN, and the World Glacier Monitoring Service. This is what the last century looks like in a single glance.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10">
        {metrics.map((m, idx) => (
          <motion.div
            key={m.key}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[color:var(--np-bg)] p-6 md:p-8 flex flex-col justify-between min-h-[180px] group hover:bg-[color:var(--np-bg-2)] transition-colors"
            data-testid={`indicator-${m.key}`}
          >
            <div className="flex items-start justify-between">
              <div className="font-mono-tech text-[9px] tracking-[0.35em] uppercase text-[color:var(--np-muted)] max-w-[70%]">
                {m.label}
              </div>
              {m.trend === "up" ? (
                <ArrowUp size={14} weight="bold" className="text-[color:var(--np-alert)]" />
              ) : (
                <ArrowDown size={14} weight="bold" className="text-[color:var(--np-water)]" />
              )}
            </div>
            <div>
              <div className="font-serif-display text-4xl md:text-5xl leading-none tracking-tight mt-6 text-[color:var(--np-text)]">
                {m.value}
              </div>
              <div className="font-mono-tech text-[10px] mt-2 text-[color:var(--np-text-2)] uppercase tracking-wider">{m.unit}</div>
              <div className="font-mono-tech text-[9px] mt-4 text-[color:var(--np-muted)] uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity">Src · {m.source}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
